import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { useParams, useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    ArrowLeft, Plus, FileText, Send, CheckCircle, Eye,
    X, Calendar, DollarSign, Scale, Layers, Edit, Trash2,
} from 'lucide-react';

function PartnerBilling() {
    const { partnerId } = useParams<{ partnerId: string }>();
    const navigate = useNavigate();

    const [partner, setPartner] = React.useState<any>(null);
    const [agreements, setAgreements] = React.useState<any[]>([]);
    const [invoices, setInvoices] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);

    // Create/Edit Agreement Modal
    const [showAgreementModal, setShowAgreementModal] = React.useState<boolean>(false);
    const [editingAgreement, setEditingAgreement] = React.useState<any>(null); // null = create, object = edit
    const [agreementLoader, setAgreementLoader] = React.useState<boolean>(false);
    const [form, setForm] = React.useState<any>(getEmptyForm());

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = React.useState<any>(null);
    const [deleteLoader, setDeleteLoader] = React.useState<boolean>(false);

    // Generate Invoice Modal
    const [showGenerateModal, setShowGenerateModal] = React.useState<boolean>(false);
    const [generateLoader, setGenerateLoader] = React.useState<boolean>(false);
    const [invoiceForm, setInvoiceForm] = React.useState<any>({ period_start: '', period_end: '' });

    function getEmptyForm() {
        return {
            name: '', rate_type: 'flat_per_kg', rate_per_kg: '', fixed_amount: '',
            invoice_frequency: 'monthly', payment_terms_days: 30,
            effective_from: new Date().toISOString().split('T')[0], effective_to: '', notes: '',
            tiers: [{ zone: '', origin_state: '', destination_state: '', min_weight: 0, max_weight: '', rate_amount: '' }],
        };
    }

    React.useEffect(() => { loadData(); }, [partnerId]);

    const loadData = () => {
        setLoading(true);
        Promise.all([
            ApiGatewayApis.getSinglePartner(partnerId).catch(() => null),
            ApiGatewayApis.getPartnerRateAgreements(partnerId).catch(() => null),
            ApiGatewayApis.getPartnerInvoices(partnerId).catch(() => null),
        ]).then(([partnerRes, agreementsRes, invoicesRes]) => {
            setLoading(false);
            if (partnerRes?.data?.data) setPartner(partnerRes.data.data);
            if (agreementsRes?.data?.data) setAgreements(agreementsRes.data.data);
            if (invoicesRes?.data?.data) setInvoices(invoicesRes.data.data);
        });
    };

    const openCreateModal = () => {
        setEditingAgreement(null);
        setForm(getEmptyForm());
        setShowAgreementModal(true);
    };

    const openEditModal = (ag: any) => {
        setEditingAgreement(ag);
        setForm({
            name: ag.name || '',
            rate_type: ag.rate_type || 'flat_per_kg',
            rate_per_kg: ag.rate_per_kg || '',
            fixed_amount: ag.fixed_amount || '',
            invoice_frequency: ag.invoice_frequency || 'monthly',
            payment_terms_days: ag.payment_terms_days || 30,
            effective_from: ag.effective_from || '',
            effective_to: ag.effective_to || '',
            notes: ag.notes || '',
            is_active: ag.is_active,
            tiers: ag.tiers && ag.tiers.length > 0
                ? ag.tiers.map((t: any) => ({ zone: t.zone || '', origin_state: t.origin_state || '', destination_state: t.destination_state || '', min_weight: t.min_weight || 0, max_weight: t.max_weight || '', rate_amount: t.rate_amount || '' }))
                : [{ zone: '', origin_state: '', destination_state: '', min_weight: 0, max_weight: '', rate_amount: '' }],
        });
        setShowAgreementModal(true);
    };

    const saveAgreement = () => {
        setAgreementLoader(true);
        const payload: any = { ...form };
        if (form.rate_type !== 'zone_weight_tier') delete payload.tiers;
        if (!payload.effective_to) delete payload.effective_to;

        const promise = editingAgreement
            ? ApiGatewayApis.updateRateAgreement(editingAgreement.id, payload)
            : ApiGatewayApis.createPartnerRateAgreement(partnerId, payload);

        promise.then((response: any) => {
            setAgreementLoader(false);
            if (response?.data?.success) {
                toast.success(editingAgreement ? 'Rate agreement updated' : 'Rate agreement created');
                setShowAgreementModal(false);
                loadData();
            }
        }).catch((err: any) => {
            setAgreementLoader(false);
            toast.error(err?.response?.data?.message || 'Failed to save agreement');
        });
    };

    const deleteAgreement = () => {
        if (!showDeleteModal) return;
        setDeleteLoader(true);
        ApiGatewayApis.deleteRateAgreement(showDeleteModal.id)
            .then((response: any) => {
                setDeleteLoader(false);
                if (response?.data?.success) {
                    toast.success('Rate agreement deleted');
                    setShowDeleteModal(null);
                    loadData();
                }
            }).catch((err: any) => {
                setDeleteLoader(false);
                toast.error(err?.response?.data?.message || 'Failed to delete agreement');
            });
    };

    const generateInvoice = () => {
        setGenerateLoader(true);
        ApiGatewayApis.generatePartnerInvoice(partnerId, invoiceForm)
            .then((response: any) => {
                setGenerateLoader(false);
                if (response?.data?.success) {
                    toast.success('Invoice generated successfully');
                    setShowGenerateModal(false);
                    loadData();
                }
            }).catch((err: any) => {
                setGenerateLoader(false);
                toast.error(err?.response?.data?.message || 'Failed to generate invoice');
            });
    };

    const addTier = () => {
        setForm({ ...form, tiers: [...form.tiers, { zone: '', origin_state: '', destination_state: '', min_weight: 0, max_weight: '', rate_amount: '' }] });
    };
    const updateTier = (index: number, field: string, value: any) => {
        const tiers = [...form.tiers];
        tiers[index] = { ...tiers[index], [field]: value };
        setForm({ ...form, tiers });
    };
    const removeTier = (index: number) => {
        setForm({ ...form, tiers: form.tiers.filter((_: any, i: number) => i !== index) });
    };

    const rateTypeLabels: any = {
        flat_per_kg: 'Flat rate per kg', fixed_per_shipment: 'Fixed per shipment',
        zone_weight_tier: 'Zone / weight tiers', custom_amount: 'Custom flat amount',
    };

    const getStatusBadge = (status: string) => {
        const styles: any = {
            draft: 'bg-gray-100 text-gray-600', sent: 'bg-blue-100 text-blue-700',
            viewed: 'bg-purple-100 text-purple-700', paid: 'bg-green-100 text-green-700',
            overdue: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-500',
        };
        return <span className={`px-2 py-1 rounded-full text-[11px] font-semibold ${styles[status] || 'bg-gray-100'}`}>{status?.toUpperCase()}</span>;
    };

    if (loading) {
        return (
            <>
                <AdminSidebar title="Partner Billing" />
                <div className="relative md:ml-64 bg-[#FAFAFA]">
                    <AdminNavbar title="Partner Billing" />
                    <div className="flex justify-center py-32">
                        <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <AdminSidebar title="Partner Billing" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title={`Billing — ${partner?.company_name || 'Partner'}`} />
                <ToastContainer autoClose={3000} />
                <div className='lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen'>

                    {/* Back */}
                    <div className="lg:pt-5 pt-3 mb-4">
                        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#E04343] text-[14px] hover:underline">
                            <ArrowLeft size={16} /> Back to Partner Details
                        </button>
                    </div>

                    {/* ── Rate Agreements ── */}
                    <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[24px] mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-3">
                                <Scale size={20} className="text-[#E04343]" />
                                <h3 className="text-[18px] font-[700]">Rate Agreements</h3>
                            </div>
                            <button onClick={openCreateModal}
                                className="flex items-center gap-2 px-4 py-2 bg-[#E04343] text-white rounded-[8px] text-[13px] hover:bg-[#c93636]">
                                <Plus size={14} /> New Agreement
                            </button>
                        </div>

                        {agreements.length === 0 ? (
                            <p className="text-[#999] text-[13px] py-6 text-center">No rate agreements yet. Create one to start invoicing.</p>
                        ) : (
                            <div className="space-y-3">
                                {agreements.map((ag: any) => (
                                    <div key={ag.id} className={`border rounded-[8px] p-4 ${ag.is_active ? 'border-green-300 bg-green-50/30' : 'border-[#E2E8F0]'}`}>
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-[600] text-[14px]">{ag.name}</h4>
                                                    {ag.is_active && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-[600]">ACTIVE</span>}
                                                    {!ag.is_active && <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px] font-[600]">INACTIVE</span>}
                                                </div>
                                                <p className="text-[12px] text-[#999] mt-1">
                                                    {rateTypeLabels[ag.rate_type]} · Invoice {ag.invoice_frequency} · Net {ag.payment_terms_days} days
                                                </p>
                                                {ag.rate_type === 'flat_per_kg' && <p className="text-[13px] mt-1 font-[500]">₦{Number(ag.rate_per_kg).toLocaleString()}/kg</p>}
                                                {(ag.rate_type === 'fixed_per_shipment' || ag.rate_type === 'custom_amount') && <p className="text-[13px] mt-1 font-[500]">₦{Number(ag.fixed_amount).toLocaleString()} per shipment</p>}
                                                {ag.rate_type === 'zone_weight_tier' && ag.tiers && (
                                                    <p className="text-[12px] text-[#999] mt-1">{ag.tiers.length} tier{ag.tiers.length !== 1 ? 's' : ''} configured</p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[11px] text-[#999] mr-2">
                                                    From {ag.effective_from?.split('T')[0]}{ag.effective_to ? ` to ${ag.effective_to?.split('T')[0]}` : ''}
                                                </span>
                                                <button onClick={() => openEditModal(ag)} title="Edit"
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                                                    <Edit size={14} />
                                                </button>
                                                <button onClick={() => setShowDeleteModal(ag)} title="Delete"
                                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ── Invoices ── */}
                    <div className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[24px]">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-3">
                                <FileText size={20} className="text-[#E04343]" />
                                <h3 className="text-[18px] font-[700]">Invoices</h3>
                            </div>
                            <button onClick={() => setShowGenerateModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-[#E04343] text-white rounded-[8px] text-[13px] hover:bg-[#c93636]">
                                <Plus size={14} /> Generate Invoice
                            </button>
                        </div>

                        {invoices.length === 0 ? (
                            <p className="text-[#999] text-[13px] py-6 text-center">No invoices generated yet.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left text-gray-500">
                                    <thead className="text-[12px] text-[#808080] bg-[#FAFAFA]">
                                        <tr>
                                            <th className="px-2 py-2">Invoice #</th>
                                            <th className="px-2 py-2">Period</th>
                                            <th className="px-2 py-2">Shipments</th>
                                            <th className="px-2 py-2">Subtotal</th>
                                            <th className="px-2 py-2">Total</th>
                                            <th className="px-2 py-2">NIPOST Cost</th>
                                            <th className="px-2 py-2">Margin</th>
                                            <th className="px-2 py-2">Status</th>
                                            <th className="px-2 py-2">Due</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((inv: any) => {
                                            const margin = Number(inv.subtotal || 0) - Number(inv.nipost_cost || 0);
                                            return (
                                                <tr key={inv.id} className="bg-white border-b text-[13px] hover:bg-[#FAFAFA]">
                                                    <td className="px-2 py-3 font-mono text-[12px] font-[600]">{inv.invoice_number}</td>
                                                    <td className="px-2 py-3 text-[12px]">{inv.period_start} – {inv.period_end}</td>
                                                    <td className="px-2 py-3">{inv.total_shipments}</td>
                                                    <td className="px-2 py-3">₦{Number(inv.subtotal || 0).toLocaleString()}</td>
                                                    <td className="px-2 py-3 font-[600]">₦{Number(inv.total_amount || 0).toLocaleString()}</td>
                                                    <td className="px-2 py-3 text-[#999]">₦{Number(inv.nipost_cost || 0).toLocaleString()}</td>
                                                    <td className={`px-2 py-3 font-[600] ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>₦{margin.toLocaleString()}</td>
                                                    <td className="px-2 py-3">{getStatusBadge(inv.status)}</td>
                                                    <td className="px-2 py-3 text-[12px]">{inv.due_date}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ── Create/Edit Agreement Modal ── */}
                    {showAgreementModal && (
                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center overflow-y-auto" onClick={() => setShowAgreementModal(false)}>
                            <div className="bg-white rounded-[10px] w-[560px] max-h-[90vh] overflow-y-auto p-6 my-8" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="font-[700] text-[16px]">{editingAgreement ? 'Edit Rate Agreement' : 'New Rate Agreement'}</h3>
                                    <button onClick={() => setShowAgreementModal(false)}><X size={18} /></button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-[13px] text-[#808080] mb-1">Agreement Name</label>
                                        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            placeholder="e.g. Standard Rate 2025" className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                    </div>

                                    <div>
                                        <label className="block text-[13px] text-[#808080] mb-1">Rate Type</label>
                                        <select value={form.rate_type} onChange={(e) => setForm({ ...form, rate_type: e.target.value })}
                                            disabled={!!editingAgreement}
                                            className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px] disabled:bg-gray-50 disabled:text-gray-500">
                                            <option value="flat_per_kg">Flat rate per kg</option>
                                            <option value="fixed_per_shipment">Fixed per shipment</option>
                                            <option value="zone_weight_tier">Zone / weight tiers</option>
                                            <option value="custom_amount">Custom flat amount</option>
                                        </select>
                                        {editingAgreement && <p className="text-[11px] text-[#999] mt-1">Rate type cannot be changed after creation</p>}
                                    </div>

                                    {form.rate_type === 'flat_per_kg' && (
                                        <div>
                                            <label className="block text-[13px] text-[#808080] mb-1">Rate per kg (₦)</label>
                                            <input type="number" value={form.rate_per_kg} onChange={(e) => setForm({ ...form, rate_per_kg: e.target.value })}
                                                placeholder="500" className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                        </div>
                                    )}

                                    {(form.rate_type === 'fixed_per_shipment' || form.rate_type === 'custom_amount') && (
                                        <div>
                                            <label className="block text-[13px] text-[#808080] mb-1">Fixed Amount (₦)</label>
                                            <input type="number" value={form.fixed_amount} onChange={(e) => setForm({ ...form, fixed_amount: e.target.value })}
                                                placeholder="3500" className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                        </div>
                                    )}

                                    {form.rate_type === 'zone_weight_tier' && (
                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <label className="text-[13px] text-[#808080]">Rate Tiers</label>
                                                <button onClick={addTier} className="text-[12px] text-[#E04343] flex items-center gap-1"><Plus size={12} /> Add Tier</button>
                                            </div>
                                            {form.tiers.map((tier: any, i: number) => (
                                                <div key={i} className="border border-[#E2E8F0] rounded-[8px] p-3 mb-2">
                                                    <div className="flex justify-between mb-2">
                                                        <span className="text-[11px] text-[#999]">Tier {i + 1}</span>
                                                        {form.tiers.length > 1 && <button onClick={() => removeTier(i)} className="text-red-400"><X size={14} /></button>}
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2 text-[12px]">
                                                        <input placeholder="Zone (e.g. Lagos-Abuja)" value={tier.zone} onChange={(e) => updateTier(i, 'zone', e.target.value)}
                                                            className="col-span-2 border border-[#E2E8F0] rounded px-2 py-1.5" />
                                                        <input placeholder="Origin state" value={tier.origin_state} onChange={(e) => updateTier(i, 'origin_state', e.target.value)}
                                                            className="border border-[#E2E8F0] rounded px-2 py-1.5" />
                                                        <input placeholder="Dest state" value={tier.destination_state} onChange={(e) => updateTier(i, 'destination_state', e.target.value)}
                                                            className="border border-[#E2E8F0] rounded px-2 py-1.5" />
                                                        <input type="number" placeholder="Min kg" value={tier.min_weight} onChange={(e) => updateTier(i, 'min_weight', e.target.value)}
                                                            className="border border-[#E2E8F0] rounded px-2 py-1.5" />
                                                        <input type="number" placeholder="Max kg (blank=unlimited)" value={tier.max_weight} onChange={(e) => updateTier(i, 'max_weight', e.target.value)}
                                                            className="border border-[#E2E8F0] rounded px-2 py-1.5" />
                                                        <input type="number" placeholder="Rate ₦" value={tier.rate_amount} onChange={(e) => updateTier(i, 'rate_amount', e.target.value)}
                                                            className="col-span-2 border border-[#E2E8F0] rounded px-2 py-1.5 font-[600]" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Active toggle (only in edit mode) */}
                                    {editingAgreement && (
                                        <div className="flex items-center gap-3 bg-gray-50 rounded-[8px] p-3">
                                            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                                className="w-4 h-4 accent-[#E04343]" />
                                            <label className="text-[13px]">Active</label>
                                            <span className="text-[11px] text-[#999]">— inactive agreements won't be used for invoice generation</span>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[13px] text-[#808080] mb-1">Invoice Frequency</label>
                                            <select value={form.invoice_frequency} onChange={(e) => setForm({ ...form, invoice_frequency: e.target.value })}
                                                className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]">
                                                <option value="weekly">Weekly</option>
                                                <option value="biweekly">Bi-weekly</option>
                                                <option value="monthly">Monthly</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[13px] text-[#808080] mb-1">Payment Terms</label>
                                            <select value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: Number(e.target.value) })}
                                                className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]">
                                                <option value={7}>Net 7</option>
                                                <option value={15}>Net 15</option>
                                                <option value={30}>Net 30</option>
                                                <option value={45}>Net 45</option>
                                                <option value={60}>Net 60</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[13px] text-[#808080] mb-1">Effective From</label>
                                            <input type="date" value={form.effective_from?.split('T')[0] || form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                                                className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                        </div>
                                        <div>
                                            <label className="block text-[13px] text-[#808080] mb-1">Effective To (optional)</label>
                                            <input type="date" value={form.effective_to?.split('T')[0] || form.effective_to} onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
                                                className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[13px] text-[#808080] mb-1">Notes (optional)</label>
                                        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                            rows={2} placeholder="Internal notes about this agreement"
                                            className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                    </div>
                                </div>

                                <div className="flex gap-3 justify-end mt-6">
                                    <button onClick={() => setShowAgreementModal(false)}
                                        className="px-4 py-2 text-[13px] text-[#808080] border border-[#E2E8F0] rounded-[8px] hover:bg-[#FAFAFA]">Cancel</button>
                                    <button onClick={saveAgreement} disabled={agreementLoader || !form.name}
                                        className="px-4 py-2 text-[13px] bg-[#E04343] text-white rounded-[8px] hover:bg-[#c93636] disabled:opacity-50 flex items-center gap-2">
                                        {agreementLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                        {editingAgreement ? 'Update Agreement' : 'Create Agreement'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Delete Confirmation Modal ── */}
                    {showDeleteModal && (
                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowDeleteModal(null)}>
                            <div className="bg-white rounded-[10px] w-[400px] p-6" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-[700] text-[16px] text-red-600">Delete Agreement</h3>
                                    <button onClick={() => setShowDeleteModal(null)}><X size={18} /></button>
                                </div>
                                <p className="text-[13px] text-[#808080] mb-2">
                                    Are you sure you want to delete <strong>{showDeleteModal.name}</strong>?
                                </p>
                                <p className="text-[12px] text-red-500 mb-4">
                                    This cannot be undone. If invoices reference this agreement, deletion will be blocked.
                                </p>
                                <div className="flex gap-3 justify-end">
                                    <button onClick={() => setShowDeleteModal(null)}
                                        className="px-4 py-2 text-[13px] text-[#808080] border border-[#E2E8F0] rounded-[8px] hover:bg-[#FAFAFA]">Cancel</button>
                                    <button onClick={deleteAgreement} disabled={deleteLoader}
                                        className="px-4 py-2 text-[13px] bg-red-600 text-white rounded-[8px] hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                                        {deleteLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Generate Invoice Modal ── */}
                    {showGenerateModal && (
                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowGenerateModal(false)}>
                            <div className="bg-white rounded-[10px] w-[400px] p-6" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-[700] text-[16px]">Generate Invoice</h3>
                                    <button onClick={() => setShowGenerateModal(false)}><X size={18} /></button>
                                </div>
                                <p className="text-[13px] text-[#999] mb-4">
                                    Generate an invoice for all uninvoiced shipments in the selected period.
                                </p>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-[13px] text-[#808080] mb-1">Period Start</label>
                                        <input type="date" value={invoiceForm.period_start} onChange={(e) => setInvoiceForm({ ...invoiceForm, period_start: e.target.value })}
                                            className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] text-[#808080] mb-1">Period End</label>
                                        <input type="date" value={invoiceForm.period_end} onChange={(e) => setInvoiceForm({ ...invoiceForm, period_end: e.target.value })}
                                            className="w-full border border-[#E2E8F0] rounded-[8px] px-3 py-2 text-[13px]" />
                                    </div>
                                </div>
                                <div className="flex gap-3 justify-end mt-6">
                                    <button onClick={() => setShowGenerateModal(false)}
                                        className="px-4 py-2 text-[13px] text-[#808080] border border-[#E2E8F0] rounded-[8px] hover:bg-[#FAFAFA]">Cancel</button>
                                    <button onClick={generateInvoice} disabled={generateLoader || !invoiceForm.period_start || !invoiceForm.period_end}
                                        className="px-4 py-2 text-[13px] bg-[#E04343] text-white rounded-[8px] hover:bg-[#c93636] disabled:opacity-50 flex items-center gap-2">
                                        {generateLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                        Generate
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </>
    );
}

export default PartnerBilling;